import React, { useCallback } from 'react';
import { Check, X } from 'react-feather';
import { IAttachment } from 'types';
import styles from './uploader.scss';
import { JpegIcon } from 'components/svgs/JpegIcon';
import { PdfIcon } from 'components/svgs/PdfIcon';
import { PngIcon } from 'components/svgs/PngIcon';
import { CsvIcon } from 'components/svgs/CsvIcon';

interface IUploaderPreviewsProps {
  className?: string;
  itemClassName?: string;
  attachments: IAttachment[];
  onClick?(attachment: IAttachment): void;
  removeAttachment?(e: React.MouseEvent, index: number): void;
  attachmentIconClassName?: string;
}

interface IUploaderPreviewProps {
  className?: string;
  attachment: IAttachment;
  index: number;
  onClick?(attachment: IAttachment): void;
  removeAttachment?(e: React.MouseEvent, index: number): void;
  attachmentIconClassName?: string;
}

const iconMap = {
  jpeg: JpegIcon,
  jpg: JpegIcon,
  pdf: PdfIcon,
  png: PngIcon,
  csv: CsvIcon,
};

export const UploaderPreviewItem = ({ className, name, onClick }) => {
  const extension = name.split('.').pop().toLowerCase();
  const Icon = iconMap[extension];

  return (
    <div className={classnames(styles.attachment, className)} onClick={onClick}>
      <div
        className={classnames(
          styles.attachmentWrap,
          Icon && styles.transparent,
        )}
      >
        {Icon ? (
          <Icon className={styles.icon} />
        ) : (
          <div className={styles.extension}>{extension}</div>
        )}
      </div>
      <div className={styles.name} title={name}>
        {name}
      </div>
    </div>
  );
};

const UploaderPreview = ({
  attachment,
  className,
  index,
  onClick,
  removeAttachment,
  attachmentIconClassName,
}: IUploaderPreviewProps) => {
  const name = attachment.name || attachment.fileName;
  const extension = name.split('.').pop().toLowerCase();
  const Icon = iconMap[extension];

  const handleDelete = useCallback(
    (e: React.MouseEvent) => removeAttachment(e, index),
    [index, removeAttachment],
  );

  const handleClick = () => onClick && onClick(attachment);

  return (
    <div
      className={classnames(styles.attachment, className)}
      onClick={handleClick}
    >
      <div
        className={classnames(
          styles.attachmentWrap,
          Icon && styles.transparent,
        )}
      >
        {attachment.id ? (
          <Check className={styles.close} />
        ) : (
          removeAttachment && (
            <X className={styles.close} size="20" onClick={handleDelete} />
          )
        )}
        {Icon ? (
          <Icon className={classnames(styles.icon, attachmentIconClassName)} />
        ) : (
          <div className={styles.extension}>{extension}</div>
        )}
      </div>
      <div className={styles.name} title={name}>
        {name}
      </div>
    </div>
  );
};

const UploaderPreviews = ({
  attachments,
  className,
  itemClassName,
  onClick,
  removeAttachment,
  attachmentIconClassName,
}: IUploaderPreviewsProps) => (
  <div className={classnames(styles.preview, className)}>
    {attachments.map((a, idx) => (
      <UploaderPreview
        key={`UploaderPreview_${idx}`}
        attachment={a}
        index={idx}
        removeAttachment={removeAttachment}
        onClick={onClick}
        className={itemClassName}
        attachmentIconClassName={attachmentIconClassName}
      />
    ))}
  </div>
);

export default UploaderPreviews;
